/**
 * The message markup parser.
 *
 * Hand-written rather than pulled from a library for two reasons. First,
 * every renderer that takes markdown to HTML then needs a sanitiser beside
 * it, and one misconfigured sanitiser is an XSS hole in a product where
 * anyone can type into a shared channel; this produces a typed tree that
 * React renders as text nodes, so there is no path from a message to markup.
 * Second, chat only wants a subset — nobody needs reference links or
 * footnotes in a message box.
 *
 * Supported: fenced code (with language), inline code, bold, italic,
 * strikethrough, spoilers, links, mentions, blockquotes, headings, ordered
 * and unordered lists, and horizontal rules.
 */
/**
 * The two ways to address a room. `@channel` reaches everyone who can read it;
 * `@here` reaches only the people currently connected — the difference between
 * "this matters eventually" and "this matters now".
 */
export const BROADCAST_MENTIONS = ['here', 'channel'];
/** Only these schemes render as links; everything else stays literal text. */
const SAFE_SCHEME = /^(https?:\/\/|mailto:)/i;
const AUTOLINK = /\b(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"]|www\.[^\s<>()]+[^\s<>().,;:!?'"])/gi;
/**
 * Order matters: the longest delimiters are tried first so `**bold**` is not
 * read as two italics, and code is tried before everything so its contents
 * are never re-parsed.
 */
const MATCHERS = [
    { pattern: /`([^`\n]+)`/, build: (m) => ({ type: 'code', value: m[1] }) },
    {
        pattern: /\|\|([\s\S]+?)\|\|/,
        build: (m, parse) => ({ type: 'spoiler', children: parse(m[1]) }),
    },
    {
        pattern: /\*\*([^*]+)\*\*/,
        build: (m, parse) => ({ type: 'strong', children: parse(m[1]) }),
    },
    {
        pattern: /__([^_]+)__/,
        build: (m, parse) => ({ type: 'strong', children: parse(m[1]) }),
    },
    {
        pattern: /~~([^~]+)~~/,
        build: (m, parse) => ({ type: 'strike', children: parse(m[1]) }),
    },
    {
        pattern: /(?<![*\w])\*([^*\n]+)\*(?!\*)/,
        build: (m, parse) => ({ type: 'em', children: parse(m[1]) }),
    },
    {
        pattern: /(?<![_\w])_([^_\n]+)_(?!_)/,
        build: (m, parse) => ({ type: 'em', children: parse(m[1]) }),
    },
    {
        pattern: /\[([^\]\n]+)\]\(([^)\s]+)\)/,
        build: (m) => SAFE_SCHEME.test(m[2])
            ? { type: 'link', href: m[2], label: m[1] }
            : { type: 'text', value: `[${m[1]}](${m[2]})` },
    },
    {
        pattern: /<@([a-f\d]{24})>/i,
        build: (m) => ({ type: 'mention', userId: m[1], label: m[1] }),
    },
    /* Bare, because nobody types `<@here>` — and word-bounded on both sides so
       an email address or `@herewith` is left alone. */
    {
        pattern: /(?<![\w@])@(here|channel)\b/i,
        build: (m) => ({ type: 'broadcast', keyword: m[1].toLowerCase() }),
    },
    { pattern: /:([a-z0-9_+-]{2,32}):/i, build: (m) => ({ type: 'emoji', name: m[1] }) },
];
/** Splits a run of text into inline nodes. */
export function parseInline(input) {
    if (!input)
        return [];
    let earliest = null;
    for (const matcher of MATCHERS) {
        const match = matcher.pattern.exec(input);
        if (match && (earliest === null || match.index < earliest.index)) {
            earliest = { index: match.index, matcher, match };
        }
    }
    if (!earliest)
        return autolink(input);
    const { index, matcher, match } = earliest;
    const before = input.slice(0, index);
    const after = input.slice(index + match[0].length);
    return [
        ...autolink(before),
        matcher.build(match, parseInline),
        ...parseInline(after),
    ];
}
/** Turns bare URLs in a plain run into links. */
function autolink(text) {
    if (!text)
        return [];
    const nodes = [];
    let last = 0;
    AUTOLINK.lastIndex = 0;
    let match;
    while ((match = AUTOLINK.exec(text)) !== null) {
        if (match.index > last)
            nodes.push({ type: 'text', value: text.slice(last, match.index) });
        const raw = match[0];
        nodes.push({
            type: 'link',
            href: raw.startsWith('www.') ? `https://${raw}` : raw,
            label: raw,
        });
        last = match.index + raw.length;
    }
    if (last < text.length)
        nodes.push({ type: 'text', value: text.slice(last) });
    return nodes;
}
/** Parses a whole message body into blocks. */
export function parseMarkdown(body) {
    const lines = body.replace(/\r\n/g, '\n').split('\n');
    const blocks = [];
    let paragraph = [];
    const flushParagraph = () => {
        if (!paragraph.length)
            return;
        blocks.push({ type: 'paragraph', children: parseInline(paragraph.join('\n')) });
        paragraph = [];
    };
    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        // Fenced code: everything up to the closing fence is taken verbatim.
        const fence = /^```(\S*)\s*$/.exec(line);
        if (fence) {
            flushParagraph();
            const language = fence[1] || undefined;
            const collected = [];
            i += 1;
            while (i < lines.length && !/^```\s*$/.test(lines[i])) {
                collected.push(lines[i]);
                i += 1;
            }
            blocks.push({ type: 'code', language, value: collected.join('\n') });
            continue;
        }
        if (/^\s*$/.test(line)) {
            flushParagraph();
            continue;
        }
        if (/^\s*(?:---|\*\*\*|___)\s*$/.test(line)) {
            flushParagraph();
            blocks.push({ type: 'rule' });
            continue;
        }
        const heading = /^(#{1,3})\s+(.*)$/.exec(line);
        if (heading) {
            flushParagraph();
            blocks.push({
                type: 'heading',
                level: heading[1].length,
                children: parseInline(heading[2]),
            });
            continue;
        }
        if (/^\s*>\s?/.test(line)) {
            flushParagraph();
            const quoted = [];
            while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
                quoted.push(lines[i].replace(/^\s*>\s?/, ''));
                i += 1;
            }
            i -= 1;
            blocks.push({ type: 'quote', children: parseMarkdown(quoted.join('\n')) });
            continue;
        }
        const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
        const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
        if (bullet || numbered) {
            flushParagraph();
            const ordered = Boolean(numbered);
            const items = [];
            while (i < lines.length) {
                const next = ordered
                    ? /^\s*\d+[.)]\s+(.*)$/.exec(lines[i])
                    : /^\s*[-*+]\s+(.*)$/.exec(lines[i]);
                if (!next)
                    break;
                items.push(parseInline(next[1]));
                i += 1;
            }
            i -= 1;
            blocks.push({ type: 'list', ordered, items });
            continue;
        }
        paragraph.push(line);
    }
    flushParagraph();
    return blocks;
}
/**
 * Which room-wide mentions a body carries, if any.
 *
 * Shared rather than re-derived on the server so the notification pass and the
 * highlight the reader sees can never disagree about what counts as `@here`.
 */
export function parseBroadcastMentions(body) {
    const found = new Set();
    const re = /(?<![\w@])@(here|channel)\b/gi;
    let match;
    while ((match = re.exec(body)) !== null) {
        found.add(match[1].toLowerCase());
    }
    return [...found];
}
/**
 * The user ids a body personally mentions, in `<@userId>` form.
 *
 * Shared for the same reason as `parseBroadcastMentions`: the server decides
 * who gets badged from this, and the composer warns when a mention cannot
 * reach its target — the two must agree on what counts as a mention.
 */
export function parseMentions(body) {
    const ids = new Set();
    const re = /<@([a-f\d]{24})>/gi;
    let match;
    while ((match = re.exec(body)) !== null)
        ids.add(match[1]);
    return [...ids];
}
/* --------------------------- composer round-trip -------------------------- */
/**
 * The composer shows `@Ada Lovelace`; the stored message carries `<@id>`.
 * These two translate at the edges — `toComposerText` on the way in (a saved
 * draft, a message being edited), `toMessageBody` on the way out.
 *
 * Shared rather than per-client: mobile had no conversion at all, so an `@`
 * typed on a phone was plain text that notified nobody, while the same
 * message written on the web badged the person. One implementation is the
 * only way the two stay honest about what a mention is.
 */
const MENTION_TOKEN = /<@([a-f\d]{24})>/gi;
/** Widened once, because `includes` on the literal tuple rejects any string. */
const BROADCAST_WORDS = BROADCAST_MENTIONS;
export function toComposerText(body, names) {
    return body.replace(MENTION_TOKEN, (token, id) => {
        const name = names.get(id) ?? names.get(id.toLowerCase());
        return name ? `@${name}` : token;
    });
}
export function toMessageBody(text, members) {
    /* Longest first, so "Ada Lovelace" is not eaten by a member called "Ada". */
    const ordered = members
        .filter((m) => m.name.trim() && !BROADCAST_WORDS.includes(m.name.trim().toLowerCase()))
        .sort((a, b) => b.name.length - a.name.length);
    let out = text;
    for (const member of ordered) {
        out = out.replace(mentionPattern(member.name.trim()), `<@${member.id}>`);
    }
    return out;
}
/**
 * The agents a channel is allowed to mention, as mention candidates.
 *
 * Only a `room` channel can reach one. A room channel already carries the
 * `spaceId` and `zoneId` of the room it belongs to, so "which agent did you
 * mean" is answered by the floor plan rather than by a new field: the agents
 * whose desk is that room. Every other channel gets none, and an `@agent`
 * typed there stays ordinary text.
 *
 * Shaped as `{ id, name }` because that is what both the picker and
 * `toMessageBody` consume — an agent's `userId` is an ordinary user id, which
 * is the entire reason it has an account, so a mention of one needs no new
 * token and the server's `parseMentions` already resolves it.
 *
 * Lives here rather than in either client for the same reason `toMessageBody`
 * does: two implementations of "what is mentionable" would drift, and the one
 * that drifted would silently stop turning a typed name into a token.
 */
export function agentMentionUsers(agents, channel) {
    if (!channel || channel.kind !== 'room' || !channel.zoneId)
        return [];
    return agents
        .filter((agent) => agent.enabled !== false && agent.zoneId === channel.zoneId)
        .map((agent) => ({
        id: agent.userId,
        name: agent.name,
        email: '',
        /* What lets a picker label the row and a member list keep leaving it
           out — see the note on `PublicUser.kind`. An agent is offered here
           *because* this channel is the room it works in; everywhere else that
           reads `kind` still has its reason to skip it. */
        kind: 'agent',
        /* The account has its own colour, but this list is built from the agent
           records, which do not carry one. The picker only needs initials on a
           swatch, and the floor already draws agents in this indigo. */
        avatarColor: '#818cf8',
        /* An agent on the floor is online by definition — it has no
           away-from-keyboard to represent. */
        status: 'online',
    }));
}
/**
 * Itsappy AI as a mention candidate, when this account can reach it.
 *
 * Every conversation, not only a room: the assistant is the workspace's own,
 * not a resident of one floor, so the scoping rule that keeps `agentMentionUsers`
 * to a room does not apply. What does the scoping here is the status call —
 * an account without the assistant, or a deployment without a key, is told so
 * and gets no row, which is the same explained absence the Ask panel gives.
 *
 * Shaped and named from the status rather than from a constant the client
 * holds, so the typed name always matches the account `toMessageBody` turns
 * it into.
 */
export function assistantMentionUser(status, name) {
    if (!status?.enabled || !status.userId)
        return [];
    return [
        {
            id: status.userId,
            name,
            email: '',
            /* An agent for every purpose a client reads `kind` for: a bot badge in
               the picker, and a row a member list still leaves out. */
            kind: 'agent',
            avatarColor: '#818cf8',
            status: 'online',
        },
    ];
}
function mentionPattern(name) {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    /* Not preceded by a word character, `@` or `<`: an email address is not a
       mention, and neither is the `@` inside a token written on a previous pass.
       Not followed by one either, so `@Ada` leaves `@Adam` alone. */
    return new RegExp(`(?<![\\w@<])@${escaped}(?![\\w])`, 'gi');
}
/** True when the body has anything worth running the parser over. */
export function hasMarkup(body) {
    return /(\*\*|__|~~|`|\|\||^\s*[-*+]\s|^\s*\d+[.)]\s|^\s*>|^#{1,3}\s|\[[^\]]+\]\()/m.test(body);
}
/* --------------------------- task references ----------------------------- */
/**
 * A task comment can point at another task, the way it can point at a person.
 *
 * Stored as `<#taskId>` for the same reason a mention is stored as `<@userId>`:
 * the id outlives a renamed task, so the reader is always shown what the task
 * is called now rather than what it was called when someone typed it.
 *
 * Task references live here rather than in `parseInline` deliberately. Chat has
 * no way to resolve a task's title, so a token pasted into a message would
 * render as nothing at all; comments carry the titles they need beside the
 * body, and are parsed by `parseCommentBody` below.
 */
const TASK_REF_TOKEN = /<#([a-f\d]{24})>/gi;
/** The tasks a comment body points at, in `<#taskId>` form. */
export function parseTaskRefs(body) {
    const ids = new Set();
    let match;
    TASK_REF_TOKEN.lastIndex = 0;
    while ((match = TASK_REF_TOKEN.exec(body)) !== null)
        ids.add(match[1]);
    return [...ids];
}
/**
 * The composer shows `#12`; the stored comment carries `<#taskId>`.
 *
 * Only tasks picked from the suggestion list are converted. A number typed by
 * hand stays literal text, because `#12` means a different task in every
 * project and guessing which one was meant is worse than leaving it alone.
 */
export function toTaskRefBody(text, tasks) {
    if (!tasks.length)
        return text;
    /* Later picks win: re-picking after a correction is the more recent
       intention, and both entries only ever collide on the same `#number`. */
    const byNumber = new Map(tasks.map((task) => [task.number, task.id]));
    /* Not preceded by a word character or `#`, so `abc#3` and the `#` of a
       token written on a previous pass are both left alone. */
    return text.replace(/(?<![\w#])#(\d+)\b/g, (whole, digits) => {
        const id = byNumber.get(Number(digits));
        return id ? `<#${id}>` : whole;
    });
}
/**
 * Both halves of a comment's outgoing conversion — `@Ada` to `<@id>` and `#12`
 * to `<#id>` — so a composer cannot apply one and forget the other.
 */
export function toCommentBody(text, members, tasks) {
    return toTaskRefBody(toMessageBody(text, members), tasks);
}
/**
 * Splits a comment into text and the things it points at.
 *
 * Comments are deliberately not run through the markdown parser: they have
 * always rendered as typed, and turning every `*` in a stack trace into
 * emphasis is not an improvement anyone asked for. This finds the two tokens
 * a comment can carry and leaves the rest of the body exactly as it is.
 */
export function parseCommentBody(body) {
    const segments = [];
    const token = /<([@#])([a-f\d]{24})>/gi;
    let last = 0;
    let match;
    while ((match = token.exec(body)) !== null) {
        if (match.index > last) {
            segments.push({ type: 'text', value: body.slice(last, match.index) });
        }
        segments.push(match[1] === '@'
            ? { type: 'mention', userId: match[2] }
            : { type: 'task', taskId: match[2] });
        last = match.index + match[0].length;
    }
    if (last < body.length)
        segments.push({ type: 'text', value: body.slice(last) });
    return segments;
}
